import "@fontsource/plus-jakarta-sans/400.css";
import "@fontsource/plus-jakarta-sans/600.css";
import "@fontsource/plus-jakarta-sans/800.css";
import { library } from "@fortawesome/fontawesome-svg-core";
import "@fortawesome/fontawesome-svg-core/styles.css";
import {
    faGoogle,
    faJs,
    faMarkdown,
    faPython,
} from "@fortawesome/free-brands-svg-icons";
import {
    faArrowAltCircleDown,
    faComments,
    faFile,
    faFileAlt,
    faFileArchive,
    faFilePdf,
    faImage,
    faWindowClose,
} from "@fortawesome/free-regular-svg-icons";
import {
    faAlignLeft,
    faAngleLeft,
    faAngleRight,
    faBold,
    faBuilding,
    faCheck,
    faCode,
    faCog,
    faCopy,
    faDatabase,
    faEdit,
    faFile as faFileSolid,
    faFolder,
    faFolderOpen,
    faHistory,
    faIndent,
    faItalic,
    faLayerGroup,
    faLink,
    faList,
    faListOl,
    faOutdent,
    faProjectDiagram,
    faQuestion,
    faQuoteLeft,
    faRedo,
    faSearch,
    faShield,
    faStrikethrough,
    faTasks,
    faTimes,
    faUnderline,
    faUndo,
    faVial,
    faVideo,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/vue-fontawesome";
import * as Sentry from "@sentry/vue";

import hljs from "highlight.js";
import mitt from "mitt";
import mixpanel from "mixpanel-browser";
import { createPinia } from "pinia";
import piniaPluginPersistedstate from "pinia-plugin-persistedstate";
import { createApp } from "vue";

import App from "@/App.vue";
import config from "@/config";
import "@/firebase";
import router from "@/router";
import FontAwesomeButton from "@/shared/FontAwesomeButton.vue";

import { initPrimeVue } from "./primevue/init";

const emitter = mitt();

// create an instance of the Mixpanel object
mixpanel.init(config.mixpanel.token ?? "dummy", {
    debug: false,
    // This honors the Do Not Track setting in the browser. Need to set this to false for GDPR compliance.
    ignore_dnt: false,
    // This is the EU server. We need to use this for GDPR compliance.
    api_host: "https://api-eu.mixpanel.com",
    record_sessions_percent: config.mixpanel.record_sessions_percent,
    // Completely hide elements marked with this class from recordings.
    // Used for elements like TOTP secret keys that are displayed as plaintext.
    record_block_selector: ".sensitive",
    record_network: config.mixpanel.record_network,
    record_network_options: {
        // Capture requests that occurred before recording started
        recordInitialRequests: true,
    },
    loaded: (mixpanel) => {
        if (mixpanel.get_config("token") === "dummy") {
            mixpanel.disable();
        }
    },
});

export const app = createApp(App);
initPrimeVue(app);
app.use(router);

const pinia = createPinia();
pinia.use(piniaPluginPersistedstate);

app.use(pinia);

if (import.meta.env.VITE_SENTRY_DSN && import.meta.env.VITE_SENTRY_ENV) {
    Sentry.init({
        app,
        dsn: import.meta.env.VITE_SENTRY_DSN,
        environment: import.meta.env.VITE_SENTRY_ENV,
        integrations: [Sentry.replayIntegration()],

        // Session Replay
        replaysSessionSampleRate: 0.1, // This sets the sample rate at 10%. You may want to change it to 100% while in development and then sample at a lower rate in production.
        replaysOnErrorSampleRate: 1.0, // If you're not already sampling the entire session, change the sample rate to 100% when sampling sessions where errors occur.
    });
}

// Mitt
app.config.globalProperties.emitter = emitter;

// Highlight.js
hljs.registerAliases("bpmn", { languageName: "xml" });

app.component("FontAwesomeIcon", FontAwesomeIcon);
app.component("FontAwesomeButton", FontAwesomeButton);
library.add(
    faAlignLeft,
    faAngleLeft,
    faAngleRight,
    faArrowAltCircleDown,
    faBold,
    faBuilding,
    faCheck,
    faCode,
    faCog,
    faComments,
    faCopy,
    faDatabase,
    faEdit,
    faFile,
    faFileAlt,
    faFileArchive,
    faFilePdf,
    faFileSolid,
    faFolder,
    faFolderOpen,
    faGoogle,
    faHistory,
    faImage,
    faIndent,
    faItalic,
    faJs,
    faLayerGroup,
    faLink,
    faList,
    faListOl,
    faMarkdown,
    faOutdent,
    faProjectDiagram,
    faPython,
    faQuestion,
    faQuoteLeft,
    faRedo,
    faSearch,
    faShield,
    faStrikethrough,
    faTasks,
    faTimes,
    faUnderline,
    faUndo,
    faVial,
    faVideo,
    faWindowClose
);

app.mount("#app");
